import React, { FC, useState, useEffect, useRef } from 'react';
import styles from './ApplicationPage.module.scss';
import { ATMHeader } from "components/atoms/ATMHeader/ATMHeader.component";
import { useNavigate } from "react-router-dom";
import { ATMForm } from "components/atoms/ATMForm/ATMForm.component";
import SubmitApplication from 'features/application/SubmitApplication/SubmitApplication';
import HeaderSection from 'components/HeaderSection/HeaderSection.component';
import ApplicationComposite from 'features/application/ApplicationComposite/ApplicationComposite';
import { useApplicationData } from 'contexts/ApplicationDataContext';
import { pageTitle } from 'constants/HomePage/home';
import {
    validateFumigationApplication,
    EMPTY_VALIDATION_RESULT,
    ValidationResult,
} from 'features/application/validation/fumigationValidation';

interface ApplicationPageProps {

}

const ApplicationPage: React.FC<ApplicationPageProps> = () => {


    const MESSAGING_45_DAYS = "Upcoming Planned Maintenance - Unavailable September 2-8|The Fumigation portal will be unavailable September 2-8 due to planned improvements. Thank you for your patience."
    const MESSAGING_2_WEEKS = "Planned Maintenance: System improvements are underway." + " The Fumigation portal will be unavailable September 2-8. Thank you for your patience."
    const MESSAGING_LEARN_MORE_URL = "https://www.socalgas.com/scheduled-system-improvements"


    const [messagingTitle, messagingDetails] = MESSAGING_2_WEEKS.split('|');

    const { applicationData, setApplicationData, checkedTermsAndConditions, setCheckedTermsAndConditions } = useApplicationData();
    const formRef = React.createRef<HTMLFormElement>();
    const navigate = useNavigate();

    // Refs for scrolling to error sections
    const fumigationCompanyRef = useRef<HTMLDivElement>(null);
    const customerInformationRef = useRef<HTMLDivElement>(null);
    const termsAndConditionsRef = useRef<HTMLDivElement>(null);

    // Validation state — only surfaced after the first attempt to continue.
    const [validation, setValidation] = useState<ValidationResult>(EMPTY_VALIDATION_RESULT);
    const [hasAttempted, setHasAttempted] = useState<boolean>(false);

    // Once the user has attempted to continue, re-validate as they fix things so
    // the messages clear themselves without a second click.
    useEffect(() => {
        if (!hasAttempted) return;
        setValidation(validateFumigationApplication(applicationData, checkedTermsAndConditions));
    }, [applicationData, checkedTermsAndConditions, hasAttempted]);

    const scrollToFirstError = (result: ValidationResult) => {
        // Determine which section to scroll to
        let targetRef: React.RefObject<HTMLDivElement> | null = null;
        let sectionName = '';

        if (result.formErrors.length > 0 || Object.keys(result.headerFieldErrors).length > 0) {
            // Header field errors are in the Fumigation Company section
            targetRef = fumigationCompanyRef;
            sectionName = 'Fumigation Company';
        } else if (result.lineErrors.length > 0 || Object.keys(result.recordFieldErrors).length > 0) {
            // Line errors are in the Customer Information section
            targetRef = customerInformationRef;
            sectionName = 'Customer Information';
        } else if (result.termsError) {
            // Terms error is in the Terms and Conditions section
            targetRef = termsAndConditionsRef;
            sectionName = 'Terms and Conditions';
        }

        if (targetRef?.current) {
            console.log(`Scrolling to ${sectionName} section`, targetRef.current);

            // Try multiple scroll approaches for better browser compatibility
            // Approach 1: scrollIntoView
            targetRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest'
            });

            // Approach 2: Manual scroll as fallback (after a short delay)
            setTimeout(() => {
                if (targetRef?.current) {
                    const elementTop = targetRef.current.getBoundingClientRect().top + window.pageYOffset;
                    const offset = 100; // Scroll slightly above the element
                    window.scrollTo({
                        top: elementTop - offset,
                        behavior: 'smooth'
                    });
                }
            }, 50);
        } else {
            console.warn('Target ref not found for scrolling');
        }
    };

    const handleNext = () => {
        const result = validateFumigationApplication(applicationData, checkedTermsAndConditions);
        setValidation(result);
        setHasAttempted(true);

        if (result.isValid) {
            navigate('/reviewAndSubmit');
        } else {
            // Scroll to first error after a brief delay to allow state to update
            setTimeout(() => scrollToFirstError(result), 100);
        }
    };

    return (
        <>
            <ATMHeader className={styles.pageHeader} />
          {/*   <div className={styles.messagingBanner}>
                <h3 className={styles.messagingBannerTitle}>{messagingTitle}</h3>
                {messagingDetails && (
                    <p className={styles.messagingBannerDetails}>{messagingDetails}</p>
                )}
                <a
                    className={styles.messagingBannerLink}
                    href={MESSAGING_LEARN_MORE_URL}
                    target="_blank"
                    rel="noreferrer"
                >
                    Learn More
                </a>
            </div> */}
            <HeaderSection sections='Submit Notice of Intent to Perform Fumigation Close / Restore' />

            <div className={styles.headerTitleStyle}>
                <ATMHeader className={styles.header}>
                    {pageTitle}
                </ATMHeader>
                <span className={styles.indicatorMsg}>
                    <p className={styles.indicator}>* </p>
                    <p className={styles.indicatorText}>Indicates required field</p>
                </span>
            </div>

            <div className={styles.backgroundColor}>

                <ATMForm
                    size="tiny"
                    className="sempraTinyMarginBottom"
                    ref={formRef}
                    mode="onChange"
                    autocomplete="off"
                >

                    {({ control, setValue }) => {

                        return (
                            <>
                                <ApplicationComposite
                                    applicationData={applicationData}
                                    setApplicationData={setApplicationData}
                                    control={control}
                                    setValue={setValue}
                                    checkedTermsAndConditions={checkedTermsAndConditions}
                                    setCheckedTermsAndConditions={setCheckedTermsAndConditions}
                                    validation={validation}
                                    showValidation={hasAttempted}
                                    fumigationCompanyRef={fumigationCompanyRef}
                                    customerInformationRef={customerInformationRef}
                                    termsAndConditionsRef={termsAndConditionsRef}
                                />

                                <SubmitApplication
                                    onNext={handleNext}
                                />
                            </>
                        )
                    }}
                </ATMForm>




            </div>
        </>
    );
}

export default ApplicationPage;
